import { captureException, setContext, addBreadcrumb } from "@sentry/nextjs";
import { OrderQuery } from "~graphql/sdk";

export type CheckoutType = "standard" | "express" | "pre-auth" | "legacy";

export type CheckoutErrorCategory =
  | "payment_creation"
  | "payment_confirmation"
  | "order_update"
  | "order_polling"
  | "transaction_error"
  | "hold_order"
  | "redirect_error"
  | "stripe_error"
  | "validation_error";

export interface CheckoutErrorContext {
  checkoutType: CheckoutType;
  category: CheckoutErrorCategory;
  orderId?: string;
  orderStatus?: string;
  paymentMethod?: string;
  paymentGateway?: string;
  transactionStatus?: string;
  errorCode?: string;
  // Additional context
  [key: string]: unknown;
}

export interface CaptureCheckoutErrorOptions {
  message: string;
  error?: unknown;
  context: CheckoutErrorContext;
  fingerprint?: string[];
}

export function captureCheckoutError(
  options: CaptureCheckoutErrorOptions
): void {
  const { message, error, context, fingerprint } = options;

  try {
    // Set checkout context for this error
    setContext("checkout", {
      type: context.checkoutType,
      category: context.category,
      orderId: context.orderId,
      orderStatus: context.orderStatus,
      paymentMethod: context.paymentMethod,
      paymentGateway: context.paymentGateway,
      transactionStatus: context.transactionStatus,
    });

    captureException(new Error(message), {
      tags: {
        checkoutType: context.checkoutType,
        errorCategory: context.category,
        ...(context.errorCode && { errorCode: context.errorCode }),
      },
      extra: {
        error,
        ...context,
      },
      ...(fingerprint && { fingerprint }),
    });
  } catch (sentryError) {
    console.error("Error capturing Sentry exception:", sentryError);
  }
}

// Helper to extract order context for error logging
export function getOrderErrorContext(
  order: OrderQuery["order"] | undefined
): Partial<CheckoutErrorContext> {
  if (!order) return {};

  return {
    orderId: order.id,
    orderStatus: order.status,
    transactionStatus: order.transactionStatus,
  };
}

// Add breadcrumb for checkout flow tracking
export function addCheckoutBreadcrumb(
  message: string,
  data?: Record<string, unknown>
): void {
  addBreadcrumb({
    category: "checkout",
    message,
    level: "info",
    data,
  });
}
